import { EnergyProductId } from '../energy/energy.model';
import { EXPRESSION_VARIABLE_NAME } from '@shared/constants/expression-variables-names.constant';
import {
  ElementCategoryID,
  IElementCategoryElementsFn,
} from '@shared/models/element-categories.model';
import { IFactoryProductElement } from '@shared/models/factory-element.model';
import { ElementKind, IElement } from '@shared/models/project.model';
import { HeaterProducts } from '../heating/heating.model';

export enum CoolingPropertyName {
  Type = 'cooling_type',
  /** SEER for chillers/heat pumps in cooling mode */
  NominalEfficiency = 'nominal_efficiency',
  /** Seasonal efficiency (ESEER/IPLV) */
  SeasonalEfficiency = 'seasonal_efficiency',
  /** Heating mode performance (only for DX) */
  COP = 'cop',
  /** District cooling efficiency (0..1) */
  Efficiency = 'cooling_efficiency',
  /** Installed max capacity (kW) */
  MaxCapacity = 'max_cooling_capacity',
}

export enum CoolingType {
  Chiller = 'chiller',
  DirectExpansion = 'direct_expansion',
  DistrictCooling = 'district_cooling',
}

const UNIT_MASS_EXPRESSION = `30 + ${CoolingPropertyName.MaxCapacity} * 10`;

const COOLING_ELECTRICITY_PRODUCT: IFactoryProductElement = {
  kind: ElementKind.Product,
  product_id: EnergyProductId.Electricity,
  count: `(energy * ${EXPRESSION_VARIABLE_NAME.BUILDING_LIFETIME}) / ${CoolingPropertyName.SeasonalEfficiency}`,
  unit: 'kWh',
};

const DISTRICT_COOLING_PRODUCT: IFactoryProductElement = {
  kind: ElementKind.Product,
  product_id: EnergyProductId.DistrictCooling,
  count: `energy * ${CoolingPropertyName.Efficiency} * ${EXPRESSION_VARIABLE_NAME.BUILDING_LIFETIME}`,
  unit: 'kWh',
};

const CHILLER_UNIT_PRODUCT: IFactoryProductElement = {
  kind: ElementKind.Product,
  product_id: HeaterProducts.HeatPumpAirToAir,
  count: UNIT_MASS_EXPRESSION,
  unit: 'kg',
  name: 'Chiller',
};

const DIRECT_EXPANSION_UNIT_PRODUCT: IFactoryProductElement = {
  kind: ElementKind.Product,
  product_id: HeaterProducts.HeatPumpAirToAir,
  count: UNIT_MASS_EXPRESSION,
  unit: 'kg',
  name: 'Direct Expansion',
};

const DISTRICT_COOLING_UNIT_PRODUCT: IFactoryProductElement = {
  kind: ElementKind.Product,
  product_id: HeaterProducts.DistrictHeating,
  count: UNIT_MASS_EXPRESSION,
  unit: 'kg',
  name: 'District Cooling',
};

/**
 * Product selection logic for cooling category
 */
export const coolingProducts: ReturnType<IElementCategoryElementsFn> = [
  // Cooling production unit product
  {
    property: CoolingPropertyName.Type,
    map: {
      [CoolingType.Chiller]: [CHILLER_UNIT_PRODUCT],
      [CoolingType.DirectExpansion]: [DIRECT_EXPANSION_UNIT_PRODUCT],
      [CoolingType.DistrictCooling]: [DISTRICT_COOLING_UNIT_PRODUCT],
    },
  },

  // Energy consumption product
  {
    property: CoolingPropertyName.Type,
    map: {
      [CoolingType.Chiller]: [COOLING_ELECTRICITY_PRODUCT],
      [CoolingType.DirectExpansion]: [COOLING_ELECTRICITY_PRODUCT],
      [CoolingType.DistrictCooling]: [DISTRICT_COOLING_PRODUCT],
    },
  },
];

export const COOLING_DEMAND_PER_INSTANCE_VARIABLE =
  'cooling_demand_per_instance';

export type ICoolingElement = IElement & {
  category_id: ElementCategoryID.Cooling;
};

/**
 * Default max capacity per m2 in kW
 * Source: https://www.polarpumpen.se/kunskapsbanken/ac-luftkonditionering-kunskapsbank/valja-ac/dimensionering/
 */
export const DEFAULT_MAX_CAPACITY_PER_M2 = 0.07; // 70 W/m2
